import { useState, useEffect, createContext, useContext } from 'react';
import { AdminUser, TwitchOAuthConfig } from '../types';
import { adminApi } from '../services/adminApi';

interface AuthContextType {
  user: AdminUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  getAuthUrl: () => Promise<string>;
  login: (code: string, state: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useAuthProvider = () => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const currentUser = adminApi.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        // Try to refresh token to ensure it's still valid
        try {
          await adminApi.refreshToken();
        } catch (error) {
          // Token expired, clear auth
          adminApi.logout();
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAuthUrl = async (): Promise<string> => {
    const { authUrl } = await adminApi.getAuthUrl();
    return authUrl;
  };

  const login = async (code: string, state: string) => {
    setIsLoading(true);
    try {
      const session = await adminApi.login(code, state);
      setUser(session.admin);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await adminApi.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAuth = async () => {
    try {
      const session = await adminApi.refreshToken();
      setUser(session.admin);
    } catch (error) {
      console.error('Token refresh failed:', error);
      setUser(null);
      throw error;
    }
  };

  return {
    user,
    isLoading,
    isAuthenticated,
    getAuthUrl,
    login,
    logout,
    refreshAuth,
  };
};

export const getTwitchOAuthUrl = (config: TwitchOAuthConfig): string => {
  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: 'code',
    scope: config.scopes.join(' '),
    state: generateRandomState(),
  });

  return `https://id.twitch.tv/oauth2/authorize?${params.toString()}`;
};

const generateRandomState = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

export { AuthContext };